import { Outlet, Link, useRouterState, useNavigate } from '@tanstack/react-router';
import { BarChart3, Mail, BookOpen, Database, Settings } from 'lucide-react';
import { ConnectedThemeToggle } from '@/components/ui/ThemeToggle';
import { MobileNav } from '@/components/molecules';
import { useLayoutContext } from '@/contexts/useLayoutContext';
import { AuthNav, type SyncStatus } from '@/features/auth';
import { useSetupStatus } from '@/features/setup';
import { useLicenseStatus } from '@/features/license';
import { LicenseBadge } from '@/features/license/components/atoms';
import { clearAuth, getStoredToken } from '@/features/auth/utils/authStorage';
import { UpdateNotification } from '@/components/ui/UpdateNotification';
import { useApiVersion, useAppVersion } from '@/hooks';
import { useSessionState } from '@/hooks/useSessionState';
import { AlertTriangle, X } from 'lucide-react';
import { useEffect, useRef } from 'react';
import leanAgilityIcon from '@lean-agility/design-system/src/assets/lean-agility-icon.svg';
import { ShipcastLogo } from '@/components/atoms/ShipcastLogo';

declare const __APP_VERSION__: string;

export function RootLayout() {
  const routerState = useRouterState();
  const navigate = useNavigate();
  const currentPath = routerState.location.pathname;

  // Check setup status for first-time setup redirect
  const { data: setupStatus, isLoading: isSetupLoading } = useSetupStatus();

  // Setup guard: redirect based on setup status
  useEffect(() => {
    if (isSetupLoading || !setupStatus) return;

    const isOnSetupPage = currentPath === '/setup';

    if (setupStatus.setup_required && !isOnSetupPage) {
      // Setup is required - but only clear auth if we don't have a valid token.
      // If we have a token, we just completed setup and are waiting for the
      // setup status query to refresh. Don't wipe the just-stored credentials!
      if (!getStoredToken()) {
        clearAuth();
      }
      // Redirect to setup page
      navigate({ to: '/setup' });
    } else if (!setupStatus.setup_required && isOnSetupPage) {
      // Setup is complete but user is on setup page - redirect to home
      navigate({ to: '/' });
    }
  }, [setupStatus, isSetupLoading, currentPath, navigate]);

  // Get shared state from context (SSE status, selected simulation, etc.)
  const { sseConnected, ssePolling, switchToPolling, reconnectSSE, hasResults, resultsCount, selectedSimulationId } =
    useLayoutContext();

  // Fetch API version for footer display
  const apiVersion = useApiVersion();

  // Detect a newer deployed bundle and offer a cache-clearing reload. Dismissal
  // is keyed to the detected version (stored per-tab), so silencing one version
  // still re-prompts when an even newer deploy lands.
  const { updateAvailable, latestVersion, reload: reloadForUpdate } = useAppVersion();
  const [dismissedVersion, setDismissedVersion] = useSessionState<string | null>('update-dismissed-version', null);
  const showUpdateBanner = updateAvailable && latestVersion !== null && latestVersion !== dismissedVersion;

  // Fetch license status for footer badge and expiry banner
  const { data: licenseStatus } = useLicenseStatus();
  const [expiryDismissed, setExpiryDismissed] = useSessionState('license-expiry-dismissed', false);

  // Measure header height and expose as CSS variable for sticky sub-headers
  const headerRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const height = entries[0]?.contentBoxSize?.[0]?.blockSize ?? el.offsetHeight;
      document.documentElement.style.setProperty('--header-h', `${height}px`);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Determine active tab from current path
  const getActiveTab = () => {
    if (currentPath === '/' || currentPath === '/howto') return 'howto';
    if (currentPath === '/simulations') return 'simulations';
    if (currentPath.startsWith('/simulation/') && currentPath.includes('/results')) return 'results';
    if (currentPath.startsWith('/simulation/')) return 'config';
    return 'howto';
  };

  const activeTab = getActiveTab();

  // Get current search params to preserve when navigating between config/results
  const currentSearch = routerState.location.search as Record<string, unknown>;

  // Compute sync status for the user menu
  const getSyncStatus = (): SyncStatus => {
    if (!selectedSimulationId) return null;
    if (sseConnected) return 'live';
    if (ssePolling) return 'polling';
    return 'connecting';
  };

  const handleSyncClick = () => {
    if (sseConnected) {
      switchToPolling();
    } else {
      reconnectSSE();
    }
  };

  return (
    <div className="bg-theme-base min-h-screen">
      {/* Background Pattern */}
      <div className="fixed inset-0 -z-10">
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(to bottom right, var(--color-gradient-start), var(--color-bg-base), var(--color-gradient-end))',
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            opacity: 'var(--pattern-opacity)',
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
        <div
          className="absolute top-0 left-1/4 size-96 rounded-full blur-3xl"
          style={{ backgroundColor: 'var(--color-glow-primary)' }}
        />
        <div
          className="absolute right-1/4 bottom-0 size-96 rounded-full blur-3xl"
          style={{ backgroundColor: 'var(--color-glow-accent)' }}
        />
      </div>

      {/* Header with Navigation */}
      <header ref={headerRef} className="border-theme bg-theme-base/80 sticky top-0 z-50 border-b backdrop-blur-xl">
        <div className="mx-auto w-full max-w-450 px-4">
          <div
            className="
              grid grid-cols-[auto_1fr_auto] items-center gap-2
              md:gap-4
            "
          >
            {/* Logo & Branding - Left */}
            <Link
              to="/"
              className="
                flex items-center gap-2 py-3 transition-opacity
                hover:opacity-90
                sm:gap-3
              "
            >
              <ShipcastLogo
                showTagline
                className="
                  h-10 w-auto
                  sm:h-13
                "
              />
            </Link>

            {/* Tab Navigation - Center (hidden below xs, hamburger menu takes over) */}
            <div
              className="
                hidden justify-center
                xs:flex
              "
            >
              <nav className="flex gap-1" aria-label="Tabs">
                <Link
                  to="/"
                  className={`
                    flex items-center gap-2 border-b-2 px-2 py-3 text-sm font-medium transition-all
                    lg:p-4
                    ${
                      activeTab === 'howto'
                        ? `
                          border-border-accent text-action-primary
                          dark:text-text-accent
                        `
                        : `
                          text-theme-muted
                          hover:text-theme-primary
                          border-transparent
                          hover:border-border-strong
                          dark:hover:border-border-strong
                        `
                    }
                  `}
                >
                  <BookOpen className="size-4" />
                  <span
                    className="
                      hidden
                      lg:inline
                    "
                  >
                    How To
                  </span>
                </Link>
                <Link
                  to="/simulations"
                  className={`
                    flex items-center gap-2 border-b-2 px-2 py-3 text-sm font-medium transition-all
                    lg:p-4
                    ${
                      activeTab === 'simulations'
                        ? `
                          border-border-accent text-action-primary
                          dark:text-text-accent
                        `
                        : `
                          text-theme-muted
                          hover:text-theme-primary
                          border-transparent
                          hover:border-border-strong
                          dark:hover:border-border-strong
                        `
                    }
                  `}
                >
                  <Database className="size-4" />
                  <span
                    className="
                      hidden
                      lg:inline
                    "
                  >
                    Simulations
                  </span>
                </Link>
                <Link
                  to={selectedSimulationId ? '/simulation/$simulationId' : '/simulations'}
                  params={selectedSimulationId ? { simulationId: selectedSimulationId } : undefined}
                  search={selectedSimulationId ? currentSearch : undefined}
                  className={`
                    flex items-center gap-2 border-b-2 px-2 py-3 text-sm font-medium transition-all
                    lg:p-4
                    ${
                      activeTab === 'config'
                        ? `
                          border-border-accent text-action-primary
                          dark:text-text-accent
                        `
                        : selectedSimulationId
                          ? `
                            text-theme-muted
                            hover:text-theme-primary
                            border-transparent
                            hover:border-border-strong
                            dark:hover:border-border-strong
                          `
                          : `
                            cursor-not-allowed border-transparent text-text-disabled
                            dark:text-text-disabled
                          `
                    }
                  `}
                >
                  <Settings className="size-4" />
                  <span
                    className="
                      hidden
                      lg:inline
                    "
                  >
                    Configuration
                  </span>
                </Link>
                <Link
                  to={selectedSimulationId ? '/simulation/$simulationId/results' : '/simulations'}
                  params={selectedSimulationId ? { simulationId: selectedSimulationId } : undefined}
                  search={selectedSimulationId ? currentSearch : undefined}
                  className={`
                    flex items-center gap-2 border-b-2 px-2 py-3 text-sm font-medium transition-all
                    lg:p-4
                    ${
                      activeTab === 'results'
                        ? `
                          border-border-accent text-action-primary
                          dark:text-text-accent
                        `
                        : hasResults || selectedSimulationId
                          ? `
                            text-theme-muted
                            hover:text-theme-primary
                            border-transparent
                            hover:border-border-strong
                            dark:hover:border-border-strong
                          `
                          : `
                            cursor-not-allowed border-transparent text-text-disabled
                            dark:text-text-disabled
                          `
                    }
                  `}
                >
                  <BarChart3 className="size-4" />
                  <span
                    className="
                      hidden
                      lg:inline
                    "
                  >
                    Results
                  </span>
                  {resultsCount > 0 && (
                    <span
                      className="
                        ml-1 hidden rounded-full bg-bg-accent-muted px-1.5 py-0.5 text-xs text-text-accent
                        lg:inline-flex
                        dark:bg-bg-accent-muted dark:text-text-accent
                      "
                    >
                      {resultsCount}
                    </span>
                  )}
                </Link>
              </nav>
            </div>

            {/* User Controls - Right */}
            <div className="flex items-center justify-end">
              {/* Desktop: Theme Toggle & Auth (hidden below xs) */}
              <div
                className="
                  hidden items-center gap-4
                  xs:flex
                "
              >
                <ConnectedThemeToggle />
                <AuthNav syncStatus={getSyncStatus()} onSyncClick={handleSyncClick} />
              </div>

              {/* Mobile: Hamburger menu (visible only below xs breakpoint) */}
              <MobileNav
                activeTab={activeTab}
                selectedSimulationId={selectedSimulationId}
                hasResults={hasResults}
                currentSearch={currentSearch}
                syncStatus={getSyncStatus()}
                onSyncClick={handleSyncClick}
              />
            </div>
          </div>
        </div>
      </header>

      {/* New-version-available Banner */}
      {showUpdateBanner && (
        <UpdateNotification onReload={reloadForUpdate} onDismiss={() => setDismissedVersion(latestVersion)} />
      )}

      {/* License Expiry Warning Banner */}
      {licenseStatus?.status === 'expiring_soon' && !expiryDismissed && (
        <div className="mx-auto w-full max-w-450 px-4 pt-4" data-testid="license-expiry-banner">
          <div
            className="
              flex items-center gap-3 rounded-lg border border-amber-500/30 bg-amber-100 px-4 py-3
              dark:bg-amber-500/20
            "
          >
            <AlertTriangle
              className="
                size-5 shrink-0 text-amber-700
                dark:text-amber-500
              "
            />
            <p
              className="
                flex-1 text-sm text-amber-700
                dark:text-amber-500
              "
            >
              Your license expires in {licenseStatus.days_remaining} days. Contact your admin to renew.
            </p>
            <button
              onClick={() => setExpiryDismissed(true)}
              className="
                rounded-sm p-1 text-amber-700
                hover:bg-amber-200
                dark:text-amber-500
                dark:hover:bg-amber-500/30
              "
              aria-label="Dismiss license warning"
              data-testid="dismiss-expiry-banner"
            >
              <X className="size-4" />
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="mx-auto w-full max-w-450 px-4 py-8">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="border-theme mt-16 border-t">
        <div className="mx-auto w-full max-w-450 px-4 py-6">
          <div
            className="
              text-theme-muted flex flex-col items-center justify-center gap-2 text-sm
              sm:flex-row
            "
          >
            <div className="flex items-center gap-2">
              <img src={leanAgilityIcon} alt="Lean-Agility" className="h-5 w-auto opacity-60" />
              <span>Created by Lean-Agility Team</span>
              <a
                href="mailto:contact@lean-agility.com"
                className="
                  inline-flex items-center gap-1 text-action-primary transition-colors
                  hover:text-action-primary-hover
                  dark:text-text-accent
                  dark:hover:text-text-accent-hover
                "
              >
                <Mail className="size-3.5" />
                contact@lean-agility.com
              </a>
            </div>
            {licenseStatus && licenseStatus.status !== 'none' && (
              <>
                <span
                  className="
                    hidden text-text-disabled
                    sm:inline
                    dark:text-text-disabled
                  "
                >
                  ·
                </span>
                <LicenseBadge status={licenseStatus.status} />
              </>
            )}
            <span
              className="
                hidden text-text-disabled
                sm:inline
                dark:text-text-disabled
              "
            >
              ·
            </span>
            <span className="text-xs text-text-muted">
              {apiVersion && apiVersion === __APP_VERSION__ ? (
                __APP_VERSION__
              ) : (
                <>
                  Frontend: {__APP_VERSION__} · API: {apiVersion ?? '...'}
                </>
              )}
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
}
